// Dashboard.js
import * as React from "react";
import { apiGet } from "api/callApi";
import SC from "./style";
import { useEffect, useState } from "react";
import ShimmerCard from "./ShimmerCard";
import NoProject from "./NoProject";
import { selectProject } from "@store/project/actions";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { selectRoles } from "@store/userRoles/actions";
import CountryDropdown from "components/CountryDropdown";
import { Checkbox, Select, Switch } from "antd";
import { isAdmin } from "utils/helper";
import { selectOccupantOption } from "@store/occupantOption/actions";
import { getCart } from "@store/cart/actions";
import theme from "@theme/";
import { getAccessControl } from "@store/accessControl/actions";
import InnoIcons from "InnoUI/innoicons";
import { ProjectCard } from "@InnoUI/";

const { Option } = Select;

const sortOptions = [
  { label: "Name (A-Z)", value: "1" },
  { label: "Name (Z-A)", value: "2" },
  { label: "Area (High - Low)", value: "3" },
  { label: "Area (Low - High)", value: "4" },
];

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [project, setProject] = useState([]);
  const [filteredProjectData, setFilteredProjectData] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sortOrder, setSortOrder] = useState(null); // Default sort order
  const [hideInactive, setHideInactive] = useState(false); // State for checkbox
  const [selectedCountry, setSelectedCountry] = useState(null); // State for country filter

  const authData = useSelector((state) => state?.authdata?.data || []);
  const currentProject = useSelector(
    (state) => state?.currentProject?.data || null,
  );
  const accessLevel = useSelector(
    (state) => state?.authdata?.data?.userType || 0,
  );

  // Get current time for greeting
  const getCurrentTime = () => {
    const now = new Date();
    const hour = now.getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  };

  const getCurrentDate = () => {
    const now = new Date();
    return now.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Get user's first name
  const getFirstName = () => {
    const fullName = authData?.fullName || "User";
    return fullName.split(" ")[0];
  };

  // Calculate statistics
  const getStats = () => {
    const totalProjects = project.length;
    const activeProjects = project.filter((p) => p.status === 1).length;
    const inactiveProjects = totalProjects - activeProjects;
    const totalOccupants = project.reduce(
      (sum, p) => sum + (p.totalOccupants || 0),
      0,
    );

    return { totalProjects, activeProjects, inactiveProjects, totalOccupants };
  };

  useEffect(() => {
    // Load filter values from sessionStorage
    sessionStorage.removeItem("firstAccess");
    const savedSortOrder = sessionStorage.getItem("sortOrder");
    const savedHideInactive =
      sessionStorage.getItem("hideInactive") === "true" ? true : false;

    setSortOrder(savedSortOrder || null);
    setHideInactive(savedHideInactive);
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        const response = await apiGet("/v1/projects", false);
        setProject(response.data);
        setFilteredProjectData(response.data);
        setIsLoading(false);
      } catch (error) {
        console.log(error);
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    //reset previous selected values
    dispatch(selectProject(null));
    dispatch(selectRoles(null));
    // dispatch(getCart(null));
  }, []);

  const handleSearch = (event) => {
    try {
      // const query = event.target.value;
      const query = event.target.value.trimStart();
      if (query === "") {
        setFilteredProjectData(project);
        return;
      }
      const newdata = project.filter(
        (item) =>
          item.propertyName &&
          item.propertyName.toLowerCase().includes(query.toLowerCase()),
      );
      setFilteredProjectData(newdata);
    } catch (error) {}
  };

  const onSelectProject = (data) => {
    // const newData = {
    //   projectId: data.id,
    //   userId: authData.id,
    // };
    // dispatch(selectRoles(newData));

    setSelectedProjectId(data.id);
    dispatch(getAccessControl(data.id));
    dispatch(selectProject(data));
    dispatch(selectOccupantOption(data.id));
  };

  useEffect(() => {
    if (
      selectedProjectId &&
      currentProject?.id &&
      String(currentProject.id) === String(selectedProjectId)
    ) {
      navigate("/project-home");
    }
  }, [currentProject?.id, navigate, selectedProjectId]);

  const applyCountryFilter = (countryId) => {
    setSelectedCountry(countryId);
  };

  const handleSort = (value) => {
    setSortOrder(value);
    if (value) {
      sessionStorage.setItem("sortOrder", value); // Save to sessionStorage
    } else {
      sessionStorage.removeItem("sortOrder");
    }
  };

  const handleHideInactiveChange = (checked) => {
    // const checked = e.target.checked;
    setHideInactive(checked);
    sessionStorage.setItem("hideInactive", checked); // Save to sessionStorage
  };

  // Centralized filter function
  const applyFilters = () => {
    let data = [...project];

    // Apply hideInactive filter
    if (hideInactive) {
      data = data.filter((item) => item.status === 1); // Filter active projects
    }

    // Apply country filter
    if (selectedCountry && selectedCountry !== "ALL") {
      data = data.filter(
        (item) =>
          item.countryName &&
          item.countryName.toLowerCase() === selectedCountry.toLowerCase(),
      );
    }

    // Apply sorting
    if (sortOrder) {
      if (sortOrder == 1) {
        // Name (A-Z)
        data.sort((a, b) => a.propertyName.localeCompare(b.propertyName));
      } else if (sortOrder == 2) {
        // Name (Z-A)
        data.sort((a, b) => b.propertyName.localeCompare(a.propertyName));
      } else if (sortOrder == 3) {
        // Area (High - Low)
        data.sort((a, b) => b.occupantArea - a.occupantArea);
      } else if (sortOrder == 4) {
        // Area (Low - High)
        data.sort((a, b) => a.occupantArea - b.occupantArea);
      }
    }

    setFilteredProjectData(data);
  };

  // Call applyFilters whenever a filter changes
  useEffect(() => {
    applyFilters();
  }, [hideInactive, selectedCountry, sortOrder, project]);

  const stats = getStats();

  return (
    <>
      {isLoading ? (
        <ShimmerCard />
      ) : project.length > 0 ? (
        <SC.Container>
          {/* Welcome Section */}
          <div
            style={{
              padding: "2rem 1.5rem 2.5rem 1.5rem",
              display: "flex",
              alignItems: "center",
              gap: "1rem",
            }}
          >
            <div
              style={{
                background: theme.gradient.themeGradient,
                borderRadius: "50%",
                width: "60px",
                height: "60px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 25px rgba(227, 110, 0, 0.3)",
              }}
            >
              <InnoIcons.DashboardIcon
                style={{
                  fontSize: "2rem",
                  color: "white",
                }}
              />
            </div>
            <div>
              <h1
                style={{
                  margin: "0",
                  fontSize: "2rem",
                  fontWeight: "700",
                  color: "#1f2937",
                  lineHeight: "1.2",
                }}
              >
                {getCurrentTime()}, {getFirstName()}!
              </h1>
              <p
                style={{
                  margin: "0.25rem 0 0 0",
                  fontSize: "1rem",
                  color: "#6b7280",
                  fontWeight: "500",
                }}
              >
                {getCurrentDate()}
              </p>
            </div>
          </div>

          {/* Statistics Cards */}
          <SC.StatsSection>
            <SC.StatCard>
              <SC.StatIcon style={{ color: theme.colors.themeColor }}>
                <InnoIcons.AccountBalanceIcon />
              </SC.StatIcon>
              <SC.StatContent>
                <SC.StatNumber>{stats.totalProjects}</SC.StatNumber>
                <SC.StatLabel>Total Projects</SC.StatLabel>
              </SC.StatContent>
            </SC.StatCard>

            <SC.StatCard>
              <SC.StatIcon style={{ color: theme.colors.themeColor }}>
                <InnoIcons.CheckCircleIcon />
              </SC.StatIcon>
              <SC.StatContent>
                <SC.StatNumber>{stats.activeProjects}</SC.StatNumber>
                <SC.StatLabel>Active Projects</SC.StatLabel>
              </SC.StatContent>
            </SC.StatCard>

            <SC.StatCard>
              <SC.StatIcon style={{ color: theme.colors.themeColor }}>
                <InnoIcons.PersonAddIcon />
              </SC.StatIcon>
              <SC.StatContent>
                <SC.StatNumber>{stats.totalOccupants}</SC.StatNumber>
                <SC.StatLabel>Total Occupants</SC.StatLabel>
              </SC.StatContent>
            </SC.StatCard>

            <SC.StatCard>
              <SC.StatIcon style={{ color: theme.colors.themeColor }}>
                <InnoIcons.ErrorIcon />
              </SC.StatIcon>
              <SC.StatContent>
                <SC.StatNumber>{stats.inactiveProjects}</SC.StatNumber>
                <SC.StatLabel>Inactive Projects</SC.StatLabel>
              </SC.StatContent>
            </SC.StatCard>
          </SC.StatsSection>

          {/* Projects Section */}
          <SC.ProjectsSection>
            <SC.TabDiv>
              <SC.ProejctContainer>
                <p
                  style={{
                    fontWeight: "700",
                    fontSize: "1.5rem",
                    margin: "0px",
                    background: theme.gradient.themeGradient,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    letterSpacing: "-0.3px",
                    lineHeight: "1.2",
                  }}
                >
                  Your Projects ({filteredProjectData.length})
                </p>

                <SC.InputContiner>
                  <SC.SearchIcon />
                  <SC.InputStyle
                    placeholder="Search by property name"
                    onChange={handleSearch}
                  />
                </SC.InputContiner>
              </SC.ProejctContainer>
              <SC.ControlsWrapper>
                {isAdmin(accessLevel) && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Switch
                      onChange={handleHideInactiveChange}
                      checked={hideInactive}
                      style={{
                        transform: "scale(0.8)",
                        backgroundColor: hideInactive
                          ? theme.colors.themeColor
                          : undefined,
                        borderColor: hideInactive
                          ? theme.colors.themeColor
                          : undefined,
                      }}
                    />
                    <span
                      style={{
                        fontSize: "14px",
                        marginLeft: "12px",
                        color: "#374151",
                        fontWeight: "500",
                      }}
                    >
                      Hide Inactive Projects
                    </span>
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  <CountryDropdown
                    onSelectCallback={applyCountryFilter}
                    width="50%"
                    style={{ height: "44px" }}
                  />
                  <SC.StyledSelect
                    placeholder="Sort By"
                    onChange={handleSort}
                    value={sortOrder}
                    allowClear
                    style={{ width: "50%" }}
                  >
                    {sortOptions.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </SC.StyledSelect>
                </div>
              </SC.ControlsWrapper>
            </SC.TabDiv>

            <SC.CardDiv>
              {filteredProjectData.map((data) => (
                <ProjectCard
                  key={data.id}
                  data={data}
                  onClick={() => onSelectProject(data)}
                />
              ))}
            </SC.CardDiv>
          </SC.ProjectsSection>
        </SC.Container>
      ) : (
        <NoProject />
      )}
    </>
  );
};

export default Dashboard;
